import { useEffect, useRef } from 'react';
import { CrosshairMode, createChart } from 'lightweight-charts';

/**
 * Client-side OHLCV chart (TradingView Lightweight Charts).
 *
 * The data is the trailing series stored in `stock_predictions.ohlcv`, so the
 * browser never calls a market-data provider. EMA levels come from the
 * pipeline's technical snapshot and are drawn as price lines.
 *
 * @param {{ohlcv: Array<{date: string, open: number, high: number, low: number,
 *   close: number, volume: number}>, indicators?: Record<string, number | string | boolean>,
 *   height?: number, symbolLabel?: string}} props
 */

const UP_COLOR = '#22c55e';
const DOWN_COLOR = '#ef4444';

const EMA_LEVELS = [
  { key: 'ema20', color: '#60a5fa' },
  { key: 'ema50', color: '#f59e0b' },
  { key: 'ema200', color: '#a855f7' },
];

function describeBar(bar) {
  if (!bar) return 'Hover the chart for OHLC details';
  return [
    `O ${bar.open.toFixed(2)}`,
    `H ${bar.high.toFixed(2)}`,
    `L ${bar.low.toFixed(2)}`,
    `C ${bar.close.toFixed(2)}`,
  ].join('   ');
}

export default function ChartWidget({ ohlcv = [], indicators = {}, height = 380, symbolLabel }) {
  const containerRef = useRef(null);
  const legendRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || ohlcv.length === 0) return undefined;

    const chart = createChart(container, {
      autoSize: true,
      layout: {
        background: { color: 'transparent' },
        textColor: '#94a3b8',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      },
      grid: {
        vertLines: { color: 'rgba(43, 54, 72, 0.35)' },
        horzLines: { color: 'rgba(43, 54, 72, 0.35)' },
      },
      rightPriceScale: { borderColor: '#1d2534' },
      timeScale: { borderColor: '#1d2534', rightOffset: 6, fixLeftEdge: true },
      crosshair: { mode: CrosshairMode.Normal },
    });

    const candles = chart.addCandlestickSeries({
      upColor: UP_COLOR,
      downColor: DOWN_COLOR,
      borderUpColor: UP_COLOR,
      borderDownColor: DOWN_COLOR,
      wickUpColor: UP_COLOR,
      wickDownColor: DOWN_COLOR,
      priceLineVisible: false,
    });
    candles.setData(
      ohlcv.map((bar) => ({
        time: bar.date,
        open: bar.open,
        high: bar.high,
        low: bar.low,
        close: bar.close,
      })),
    );

    const volumes = chart.addHistogramSeries({
      priceScaleId: 'volume',
      priceFormat: { type: 'volume' },
      priceLineVisible: false,
      lastValueVisible: false,
    });
    chart.priceScale('volume').applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
    volumes.setData(
      ohlcv.map((bar) => ({
        time: bar.date,
        value: bar.volume,
        color: bar.close >= bar.open ? 'rgba(34, 197, 94, 0.35)' : 'rgba(239, 68, 68, 0.35)',
      })),
    );

    // Precomputed EMA levels, published by the daily pipeline.
    for (const level of EMA_LEVELS) {
      const value = Number(indicators?.[level.key]);
      if (!Number.isFinite(value) || value <= 0) continue;
      candles.createPriceLine({
        price: value,
        color: level.color,
        lineWidth: 1,
        lineStyle: 2,
        axisLabelVisible: true,
        title: level.key.toUpperCase(),
      });
    }

    const lastBar = ohlcv[ohlcv.length - 1];
    const updateLegend = (bar) => {
      if (legendRef.current) legendRef.current.textContent = describeBar(bar);
    };
    updateLegend(lastBar);

    chart.subscribeCrosshairMove((param) => {
      const bar = param?.seriesData?.get(candles);
      updateLegend(bar);
    });

    chart.timeScale().fitContent();

    return () => {
      chart.remove();
    };
  }, [ohlcv, indicators]);

  return (
    <section className="panel overflow-hidden">
      <header className="panel-heading flex-wrap">
        <h2 className="panel-title">
          Price action {symbolLabel ? <span className="text-slate-500">· {symbolLabel}</span> : null}
        </h2>
        <span ref={legendRef} className="font-mono text-[11px] text-slate-500">
          Hover the chart for OHLC details
        </span>
      </header>

      {ohlcv.length === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-slate-500">
          No OHLCV series available for this instrument.
        </p>
      ) : (
        <div ref={containerRef} style={{ height }} className="w-full" />
      )}
    </section>
  );
}
